import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { accessLocalStorage, getOutboundConfigById } from '../../../services';

import PbxContext from '../../../context/PbxContext';
import '../../../libs/bulma.min.css';
import './OutboundCallsView.css';

import Loading from '../../../components/Loading/LoadingModule';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faPhone, faInfoCircle, faPen, faEye } from '@fortawesome/free-solid-svg-icons';

function OutboundCallsView() {
  const getItensStateGlobal = useContext(PbxContext);
  const { toggleIsChangeFormElements, toggleIsHidden, itemsSelectedToEdit, setItemsSelectedToEdit } = getItensStateGlobal;

  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);

  const history = useHistory();

  // Tipos de chamadas disponíveis
  const callTypeLabels = {
    support: 'Suporte',
    emergency: 'Emergência',
    freecall: 'Ligação Gratuita',
    local: 'Local',
    mobile: 'Celular',
    ddd: 'DDD',
    mobileDdd: 'Celular DDD',
    international: 'Internacional'
  };

  const callTypeDescriptions = {
    support: 'Chamadas de suporte técnico',
    emergency: 'Chamadas de emergência (190, 192, 193)',
    freecall: 'Chamadas 0800',
    local: 'Chamadas locais',
    mobile: 'Chamadas para celular local',
    ddd: 'Chamadas interurbanas',
    mobileDdd: 'Chamadas para celular interurbano',
    international: 'Chamadas internacionais'
  };

  const validateUserLogged = useCallback(async () => {
    const dataUser = await accessLocalStorage.getUserLocalStorage();
    if (!dataUser) return history.push('/');
    
    const { user: { role } } = dataUser;
    if (dataUser && role !== 'root') return history.push('/home');
    
    // Verificar se temos dados para visualizar
    if (!itemsSelectedToEdit || !itemsSelectedToEdit.id) {
      alert('Nenhuma configuração selecionada para visualização');
      return history.push('/config/chamadas/saida');
    }

    // Buscar dados completos da configuração
    const configResponse = await getOutboundConfigById(itemsSelectedToEdit.id);
    if (!configResponse || !configResponse.success) {
      alert('Erro ao carregar dados da configuração');
      return history.push('/config/chamadas/saida');
    }

    setConfig(configResponse.data);
    setLoading(false);
  }, [history, itemsSelectedToEdit]);

  useEffect(() => {
    validateUserLogged();
  }, [validateUserLogged]);

  const handleBack = () => {
    history.push('/config/chamadas/saida');
  };

  const handleEdit = () => {
    setItemsSelectedToEdit(config);
    toggleIsChangeFormElements();
    toggleIsHidden();
    history.push('/config/chamadas/saida/editar');
  };

  if (loading) {
    return <Loading />;
  }

  if (!config) {
    return (
      <div className="outbound-view-container">
        <div className="empty-state">
          <div className="empty-state-icon">
            <FontAwesomeIcon icon={faPhone} />
          </div>
          <p className="empty-state-title">Configuração não encontrada</p>
          <button className="btn-back-view" onClick={handleBack}>
            <FontAwesomeIcon icon={faTimes} />
            Voltar à Lista
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="outbound-view-container">
      <div className="outbound-view-header">
        <div className="outbound-view-title-wrapper">
          <svg className="outbound-view-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
          </svg>
          <div>
            <h1 className="outbound-view-title">Visualizar Configuração de Chamada de Saída</h1>
            <p className="outbound-view-subtitle">Detalhes da rota de chamadas</p>
          </div>
        </div>
        <div className="outbound-view-actions">
          <button className="btn-action-view btn-edit-view" onClick={handleEdit}>
            <FontAwesomeIcon icon={faPen} />
            Editar
          </button>
          <button className="btn-action-view btn-back-view" onClick={handleBack}>
            <FontAwesomeIcon icon={faTimes} />
            Voltar
          </button>
        </div>
      </div>

      <div className="details-grid">
        <div className="details-card">
          <h2 className="details-section-title">
            <FontAwesomeIcon icon={faInfoCircle} className="details-section-icon" />
            Informações Básicas
          </h2>
          
          <div className="detail-field">
            <label className="detail-label">ID</label>
            <div className="detail-value">
              <span className="detail-value-badge badge-mask">#{config.id}</span>
            </div>
          </div>

          <div className="detail-field">
            <label className="detail-label">Tenant</label>
            <div className="detail-value">
              <span className="detail-value-badge badge-tenant">{config.tenant}</span>
            </div>
          </div>

          <div className="detail-field">
            <label className="detail-label">Tipo de Chamada</label>
            <div className="detail-value">
              <span className={`detail-value-badge badge-${config.callType}`}>
                {callTypeLabels[config.callType] || config.callType}
              </span>
            </div>
            <p className="detail-value-help">
              {callTypeDescriptions[config.callType] || 'Tipo personalizado'}
            </p>
          </div>

          <div className="detail-field">
            <label className="detail-label">Status</label>
            <div className="detail-value">
              <span className={`detail-value-badge ${config.active ? 'badge-active' : 'badge-inactive'}`}>
                {config.active ? 'Ativo' : 'Inativo'}
              </span>
            </div>
          </div>
        </div>

        <div className="details-card">
          <h2 className="details-section-title">
            <FontAwesomeIcon icon={faPhone} className="details-section-icon" />
            Configurações de Roteamento
          </h2>
          
          <div className="detail-field">
            <label className="detail-label">Máscara</label>
            <div className="detail-value">
              <span className="detail-value-badge badge-mask">{config.mask}</span>
            </div>
            <p className="detail-value-help">
              Padrão de números que ativam esta rota
            </p>
          </div>

          <div className="detail-field">
            <label className="detail-label">Tronco</label>
            <div className="detail-value">
              <span className="detail-value-badge badge-trunk">{config.trunkName}</span>
            </div>
            <p className="detail-value-help">
              Tronco SIP utilizado para as chamadas
            </p>
          </div>

          <div className="detail-field">
            <label className="detail-label">Código ISP</label>
            <div className="detail-value">
              {config.ispCode ? (
                <span className="detail-value-badge badge-isp">{config.ispCode}</span>
              ) : (
                <span className="detail-value-badge badge-light">Não definido</span>
              )}
            </div>
            <p className="detail-value-help">
              Código da operadora para chamadas DDD/internacionais
            </p>
          </div>
        </div>

        <div className="details-card">
          <h2 className="details-section-title">Configurações Técnicas</h2>
          
          <div className="detail-field">
            <label className="detail-label">Dígitos a Remover</label>
            <div className="detail-value">
              <span className="detail-value-badge badge-light">{config.digitRemove}</span>
            </div>
            <p className="detail-value-help">
              Número de dígitos removidos do início do número discado
            </p>
          </div>

          <div className="detail-field">
            <label className="detail-label">Timeout</label>
            <div className="detail-value">
              <span className="detail-value-badge badge-light">{config.timeout} segundos</span>
            </div>
            <p className="detail-value-help">
              Tempo limite para estabelecer a chamada
            </p>
          </div>

          <div className="detail-field">
            <label className="detail-label">Descrição</label>
            <div className="detail-value">
              {config.description ? (
                <div className="detail-value-text">
                  {config.description}
                </div>
              ) : (
                <span className="detail-value-empty">Nenhuma descrição fornecida</span>
              )}
            </div>
          </div>
        </div>

        <div className="details-card">
          <h2 className="details-section-title">Informações do Sistema</h2>
          
          <div className="detail-field">
            <label className="detail-label">Criado em</label>
            <div className="detail-value">
              {new Date(config.createdAt).toLocaleString('pt-BR')}
            </div>
          </div>

          <div className="detail-field">
            <label className="detail-label">Atualizado em</label>
            <div className="detail-value">
              {new Date(config.updatedAt).toLocaleString('pt-BR')}
            </div>
          </div>

          {config.createdBy && (
            <div className="detail-field">
              <label className="detail-label">Criado por</label>
              <div className="detail-value">
                <span className="detail-value-badge badge-light">{config.createdBy}</span>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="config-preview-card">
        <h2 className="details-section-title">Configuração Asterisk Gerada</h2>
        <div className="config-preview-info">
          <FontAwesomeIcon icon={faInfoCircle} style={{ marginRight: '8px' }} />
          Esta é a configuração que será gerada no arquivo <code>.conf</code> do Asterisk:
        </div>
        <div className="config-preview-code">
          <code>
            {`exten => ${config.mask.startsWith('_') ? config.mask : (config.mask.match(/[NXZ]/) ? '_' + config.mask : config.mask)},1,Gosub(dial-send-${config.tenant},s,1,(${config.ispCode || ''}$\{EXTEN:${config.digitRemove}\}@${config.trunkName},${config.timeout},rtT))`}
          </code>
        </div>
      </div>

      <div className="view-actions-footer">
        <button className="btn-action-view btn-edit-view" onClick={handleEdit}>
          <FontAwesomeIcon icon={faPen} />
          Editar Configuração
        </button>
        <button className="btn-action-view btn-back-view" onClick={handleBack}>
          <FontAwesomeIcon icon={faTimes} />
          Voltar à Lista
        </button>
      </div>
    </div>
  );
}

export default OutboundCallsView;
